/* OpenAI Ads attribution -> Shopify cart attributes
 * Purpose: preserve approved ad click params before normal checkout or Shop Pay.
 * Privacy: allow-list only, no raw query strings, no console logging of values.
 */
(function () {
  'use strict';

  var LEGACY_STORAGE_KEY = 'dv_oai_attribution_v1';
  var STORAGE_KEY = 'dv_oai_attribution_v2';
  var CLEAR_PENDING_KEY = 'dv_oai_attribution_clear_pending_v2';
  var CAPTURE_VERSION = '2026-08-24-v2';
  var PAID_EXPIRY_MS = 30 * 24 * 60 * 60 * 1000;
  var MAX_VALUE_LENGTH = 256;
  var WRITE_DEBOUNCE_MS = 300;
  var PARAM_TO_ATTRIBUTE = {
    utm_source: '_oai_utm_source',
    utm_medium: '_oai_utm_medium',
    utm_campaign: '_oai_utm_campaign',
    utm_content: '_oai_utm_content',
    utm_term: '_oai_utm_term',
    oai_ad_account_id: '_oai_ad_account_id',
    oai_campaign_id: '_oai_campaign_id',
    oai_ad_group_id: '_oai_ad_group_id',
    oai_ad_id: '_oai_ad_id',
    oppref: '_oai_oppref',
    olref: '_oai_olref',
    oppcref: '_oai_oppcref'
  };
  var COOKIE_TO_ATTRIBUTE = {
    __oppref: '_oai_oppref',
    __olref: '_oai_olref',
    __oppcref: '_oai_oppcref'
  };
  var PAID_CLICK_PARAMS = ['gclid', 'gbraid', 'wbraid'];
  var PAID_CLICK_ATTRIBUTE = '_oai_paid_click';
  var PAID_AT_ATTRIBUTE = '_oai_paid_at';
  var CAPTURE_VERSION_ATTRIBUTE = '_oai_capture_version';
  var CAPTURED_AT_ATTRIBUTE = '_oai_captured_at';
  var INTERNAL_ATTRIBUTES = [
    PAID_CLICK_ATTRIBUTE,
    PAID_AT_ATTRIBUTE,
    CAPTURE_VERSION_ATTRIBUTE,
    CAPTURED_AT_ATTRIBUTE
  ];
  var CART_CLEAR_ATTRIBUTES = Object.keys(PARAM_TO_ATTRIBUTE).map(function (param) {
    return PARAM_TO_ATTRIBUTE[param];
  }).concat(INTERNAL_ATTRIBUTES);

  var writeTimer = null;
  var lastWriteBody = '';
  var pendingCartClear = false;
  var cachedUrlSearch = null;
  var cachedUrlAttributes = null;

  function getStorage(storageName) {
    try {
      var storage = window[storageName];
      var testKey = STORAGE_KEY + '_test';
      storage.setItem(testKey, '1');
      storage.removeItem(testKey);
      return storage;
    } catch (error) {
      return null;
    }
  }

  function readAttributeObject(parsed) {
    var result = {};
    Object.keys(PARAM_TO_ATTRIBUTE).forEach(function (param) {
      var attrName = PARAM_TO_ATTRIBUTE[param];
      var value = sanitizeValue(parsed && parsed[attrName]);
      if (value) result[attrName] = value;
    });
    INTERNAL_ATTRIBUTES.forEach(function (attrName) {
      var value = sanitizeInternalValue(attrName, parsed && parsed[attrName]);
      if (value) result[attrName] = value;
    });
    return result;
  }

  function paidAtState(attributes) {
    if (!attributes[PAID_CLICK_ATTRIBUTE]) return 'none';
    var paidAt = attributes[PAID_AT_ATTRIBUTE];
    var paidAtMs = paidAt ? Date.parse(paidAt) : NaN;
    var nowMs = Date.now();
    if (!isFinite(paidAtMs) || paidAtMs > nowMs) return 'invalid';
    return nowMs - paidAtMs > PAID_EXPIRY_MS ? 'expired' : 'active';
  }

  function markCartClearPending() {
    pendingCartClear = true;
    var storage = getStorage('localStorage');
    if (!storage) return;
    try {
      storage.setItem(CLEAR_PENDING_KEY, '1');
    } catch (error) {
      /* The in-memory marker still protects this page. */
    }
  }

  function hasPendingCartClear() {
    if (pendingCartClear) return true;
    var storage = getStorage('localStorage');
    if (!storage) return false;
    try {
      pendingCartClear = storage.getItem(CLEAR_PENDING_KEY) === '1';
    } catch (error) {
      return false;
    }
    return pendingCartClear;
  }

  function clearPendingCartClear() {
    pendingCartClear = false;
    var storage = getStorage('localStorage');
    if (!storage) return;
    try {
      storage.removeItem(CLEAR_PENDING_KEY);
    } catch (error) {
      /* A stale marker only causes another safe clearing write. */
    }
  }

  function readLegacyAttributes() {
    var storage = getStorage('sessionStorage');
    if (!storage) return {};

    try {
      var raw = storage.getItem(LEGACY_STORAGE_KEY);
      if (!raw) return {};
      var result = readAttributeObject(JSON.parse(raw));
      var state = paidAtState(result);
      if (state !== 'active') {
        if (result[PAID_CLICK_ATTRIBUTE]) markCartClearPending();
        delete result[PAID_CLICK_ATTRIBUTE];
        delete result[PAID_AT_ATTRIBUTE];
      }
      if (attributeCount(result)) {
        result[CAPTURE_VERSION_ATTRIBUTE] = CAPTURE_VERSION;
      }
      return result;
    } catch (error) {
      storage.removeItem(LEGACY_STORAGE_KEY);
      return {};
    }
  }

  function readStoredAttributes() {
    var storage = getStorage('localStorage');
    if (!storage) return readLegacyAttributes();

    try {
      var raw = storage.getItem(STORAGE_KEY);
      if (!raw) return readLegacyAttributes();
      var result = readAttributeObject(JSON.parse(raw));
      var state = paidAtState(result);
      if (state === 'invalid' || state === 'expired') {
        storage.removeItem(STORAGE_KEY);
        markCartClearPending();
        return {};
      }
      if (state === 'none') delete result[PAID_AT_ATTRIBUTE];
      if (attributeCount(result)) {
        result[CAPTURE_VERSION_ATTRIBUTE] = CAPTURE_VERSION;
      }
      return result;
    } catch (error) {
      storage.removeItem(STORAGE_KEY);
      markCartClearPending();
      return {};
    }
  }

  function saveStoredAttributes(attributes) {
    var storage = getStorage('localStorage');
    if (!storage) return;

    try {
      var stored = readAttributeObject(attributes || {});
      if (paidAtState(stored) !== 'active') {
        delete stored[PAID_CLICK_ATTRIBUTE];
        delete stored[PAID_AT_ATTRIBUTE];
      }
      if (attributeCount(stored)) {
        stored[CAPTURE_VERSION_ATTRIBUTE] = CAPTURE_VERSION;
        storage.setItem(STORAGE_KEY, JSON.stringify(stored));
      } else {
        storage.removeItem(STORAGE_KEY);
      }

      var legacyStorage = getStorage('sessionStorage');
      if (legacyStorage) legacyStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch (error) {
      /* Storage can fail in private browsing. Attribution must not block checkout. */
    }
  }

  function hasOwn(obj, key) {
    return Object.prototype.hasOwnProperty.call(obj, key);
  }

  function isPlainScalar(value) {
    return typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean';
  }

  function hasLuhnCheck(value) {
    var digits = String(value).replace(/\D/g, '');
    if (digits.length < 13 || digits.length > 19) return false;

    var sum = 0;
    var shouldDouble = false;
    for (var i = digits.length - 1; i >= 0; i--) {
      var digit = parseInt(digits.charAt(i), 10);
      if (shouldDouble) {
        digit *= 2;
        if (digit > 9) digit -= 9;
      }
      sum += digit;
      shouldDouble = !shouldDouble;
    }
    return sum % 10 === 0;
  }

  function looksLikeSensitiveValue(value) {
    var text = String(value);
    var lowered = text.toLowerCase();
    var digitCount = (text.match(/\d/g) || []).length;

    if (/[\u0000-\u001F\u007F]/.test(text)) return true;
    if (/[^\s@]+@[^\s@]+\.[^\s@]+/.test(text)) return true;
    if (/[?&#]/.test(text)) return true;
    if (/(checkout|payment|card|customer|email|phone|address)[\w -]{0,24}[:=]/i.test(text)) return true;
    if (/\b\d{1,6}\s+[a-z0-9 .'-]+\s+(street|st|avenue|ave|road|rd|drive|dr|lane|ln|boulevard|blvd|court|ct|circle|cir|way)\b/i.test(text)) return true;
    if (digitCount >= 8 && /[+().\-\s]/.test(text) && /^\+?[\d().\-\s]+$/.test(text)) return true;
    if (hasLuhnCheck(text)) return true;
    if (lowered.indexOf('gid://shopify/checkout') !== -1) return true;
    if (lowered.indexOf('gid://shopify/customer') !== -1) return true;

    return false;
  }

  function sanitizeValue(value) {
    if (!isPlainScalar(value)) return '';

    var text = String(value).trim();
    if (!text) return '';
    if (looksLikeSensitiveValue(text)) return '';
    if (text.length > MAX_VALUE_LENGTH) text = text.slice(0, MAX_VALUE_LENGTH);

    return text;
  }

  /* Script-generated attributes bypass sanitizeValue: its digit heuristics
   * (Luhn) can reject ISO timestamps. Validate by exact shape instead. */
  function sanitizeInternalValue(attrName, value) {
    if (!isPlainScalar(value)) return '';

    var text = String(value).trim();
    if (attrName === PAID_CLICK_ATTRIBUTE) {
      return text === '1' ? '1' : '';
    }
    if (attrName === PAID_AT_ATTRIBUTE || attrName === CAPTURED_AT_ATTRIBUTE) {
      return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?Z$/.test(text) ? text : '';
    }
    if (attrName === CAPTURE_VERSION_ATTRIBUTE) {
      return text === CAPTURE_VERSION ? CAPTURE_VERSION : '';
    }
    return '';
  }

  function copyAttributes(attributes) {
    var result = {};
    Object.keys(attributes || {}).forEach(function (attrName) {
      result[attrName] = attributes[attrName];
    });
    return result;
  }

  function readUrlAttributes() {
    var attributes = {};
    var search = window.location && window.location.search ? window.location.search : '';
    if (search === cachedUrlSearch && cachedUrlAttributes !== null) {
      var cachedPaidState = paidAtState(cachedUrlAttributes);
      if (cachedPaidState === 'invalid' || cachedPaidState === 'expired') {
        cachedUrlAttributes = {};
        markCartClearPending();
      }
      return copyAttributes(cachedUrlAttributes);
    }
    if (!search) {
      cachedUrlSearch = search;
      cachedUrlAttributes = attributes;
      return attributes;
    }

    var params;
    try {
      params = new URLSearchParams(search);
    } catch (error) {
      cachedUrlSearch = search;
      cachedUrlAttributes = attributes;
      return attributes;
    }

    Object.keys(PARAM_TO_ATTRIBUTE).forEach(function (param) {
      if (!params.has(param)) return;
      /* Paid Shopping URLs duplicate utm keys (feed set first, Ads suffix
       * last). The last sanitizable occurrence is the paid truth. */
      var values = params.getAll(param);
      for (var i = values.length - 1; i >= 0; i--) {
        var value = sanitizeValue(values[i]);
        if (value) {
          attributes[PARAM_TO_ATTRIBUTE[param]] = value;
          break;
        }
      }
    });

    for (var i = 0; i < PAID_CLICK_PARAMS.length; i++) {
      var clickValues = params.getAll(PAID_CLICK_PARAMS[i]);
      for (var j = 0; j < clickValues.length; j++) {
        if (String(clickValues[j] || '').trim()) {
          attributes[PAID_CLICK_ATTRIBUTE] = '1';
          break;
        }
      }
      if (attributes[PAID_CLICK_ATTRIBUTE]) break;
    }

    if (attributeCount(attributes)) {
      var capturedAt = new Date().toISOString();
      attributes[CAPTURED_AT_ATTRIBUTE] = capturedAt;
      attributes[CAPTURE_VERSION_ATTRIBUTE] = CAPTURE_VERSION;
      if (attributes[PAID_CLICK_ATTRIBUTE]) {
        attributes[PAID_AT_ATTRIBUTE] = capturedAt;
      }
    }

    cachedUrlSearch = search;
    cachedUrlAttributes = attributes;
    return copyAttributes(attributes);
  }

  function mergeAttributes(base, update) {
    var merged = {};
    Object.keys(base || {}).forEach(function (key) {
      if (typeof base[key] === 'string' && base[key]) merged[key] = base[key];
    });
    Object.keys(update || {}).forEach(function (key) {
      if (typeof update[key] === 'string' && update[key]) merged[key] = update[key];
    });
    return merged;
  }

  function safeDecode(value) {
    try {
      return decodeURIComponent(String(value).replace(/\+/g, ' '));
    } catch (error) {
      return value;
    }
  }

  function readCookieAttributes() {
    var attributes = {};
    var cookieText = document.cookie || '';
    if (!cookieText) return attributes;

    cookieText.split(';').forEach(function (cookie) {
      var parts = cookie.split('=');
      var name = String(parts.shift() || '').trim();
      if (!hasOwn(COOKIE_TO_ATTRIBUTE, name)) return;

      var value = sanitizeValue(safeDecode(parts.join('=')));
      if (value) attributes[COOKIE_TO_ATTRIBUTE[name]] = value;
    });

    return attributes;
  }

  function withPendingCartClear(attributes) {
    if (!hasPendingCartClear()) return attributes;

    var result = {};
    CART_CLEAR_ATTRIBUTES.forEach(function (attrName) {
      result[attrName] = '';
    });
    Object.keys(attributes || {}).forEach(function (attrName) {
      if (typeof attributes[attrName] === 'string' && attributes[attrName]) {
        result[attrName] = attributes[attrName];
      }
    });
    return result;
  }

  function collectAttribution() {
    var stored = readStoredAttributes();
    var fromCookies = readCookieAttributes();
    var fromUrl = readUrlAttributes();
    var merged = mergeAttributes(mergeAttributes(stored, fromCookies), fromUrl);

    if (attributeCount(merged)) {
      saveStoredAttributes(merged);
    }

    return withPendingCartClear(merged);
  }

  function attributeCount(attributes) {
    return Object.keys(attributes || {}).length;
  }

  function cartUpdateUrl() {
    if (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) {
      return window.Shopify.routes.root.replace(/\/?$/, '/') + 'cart/update.js';
    }
    return '/cart/update.js';
  }

  function writeAttributes(reason) {
    var attributes = collectAttribution();
    if (!attributeCount(attributes) || typeof fetch !== 'function') {
      return Promise.resolve(false);
    }

    var body = JSON.stringify({ attributes: attributes });
    if (body === lastWriteBody && reason !== 'checkout') {
      return Promise.resolve(false);
    }
    try {
      return fetch(cartUpdateUrl(), {
        method: 'POST',
        credentials: 'same-origin',
        keepalive: true,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: body
      }).then(function (response) {
        if (!response || !response.ok) {
          return false;
        }
        lastWriteBody = body;
        clearPendingCartClear();
        return true;
      }).catch(function () {
        return false;
      });
    } catch (error) {
      return Promise.resolve(false);
    }
  }

  function scheduleWrite(reason, delay) {
    if (writeTimer) {
      window.clearTimeout(writeTimer);
    }
    writeTimer = window.setTimeout(function () {
      writeTimer = null;
      writeAttributes(reason);
    }, typeof delay === 'number' ? delay : WRITE_DEBOUNCE_MS);
  }

  function captureAndStoreAttribution() {
    if (attributeCount(collectAttribution())) {
      scheduleWrite('page-load', 0);
    }
  }

  function textIncludesCheckoutSignal(value) {
    var text = String(value || '').toLowerCase();
    return text.indexOf('checkout') !== -1 || text.indexOf('shop pay') !== -1 || text.indexOf('shoppay') !== -1;
  }

  function isCheckoutIntent(target) {
    var node = target;
    var hops = 0;
    while (node && node !== document && hops < 8) {
      if (node.getAttribute) {
        var name = node.getAttribute('name');
        var id = node.getAttribute('id');
        var className = node.getAttribute('class');
        var ariaLabel = node.getAttribute('aria-label');
        var href = node.getAttribute('href');
        var testId = node.getAttribute('data-testid');

        if (name === 'checkout') return true;
        if (href && (/\/checkout\b|\/checkouts\b|shop_pay|shoppay/i).test(href)) return true;
        if (textIncludesCheckoutSignal(id)) return true;
        if (textIncludesCheckoutSignal(className)) return true;
        if (textIncludesCheckoutSignal(ariaLabel)) return true;
        if (textIncludesCheckoutSignal(testId)) return true;
        if (className && /shopify-payment-button|additional-checkout-buttons/i.test(className)) return true;
      }
      node = node.parentNode;
      hops += 1;
    }
    return false;
  }

  function bindCartUpdateEvent() {
    try {
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
        subscribe(PUB_SUB_EVENTS.cartUpdate, function () {
          scheduleWrite('cart-update', 0);
        });
      }
    } catch (error) {
      /* Pub/sub is optional. */
    }
  }

  function bindCheckoutHooks() {
    var lastPointerCheckoutTarget = null;
    var lastPointerCheckoutAt = 0;
    var pointerClickDedupMs = 1500;

    var writeBeforeNavigation = function (event) {
      if (!event || !isCheckoutIntent(event.target)) return;

      var now = Date.now();
      if (
        event.type === 'click' &&
        event.target === lastPointerCheckoutTarget &&
        now - lastPointerCheckoutAt <= pointerClickDedupMs
      ) {
        lastPointerCheckoutTarget = null;
        lastPointerCheckoutAt = 0;
        return;
      }

      if (event.type === 'pointerdown') {
        lastPointerCheckoutTarget = event.target;
        lastPointerCheckoutAt = now;
      } else if (event.type === 'click') {
        lastPointerCheckoutTarget = null;
        lastPointerCheckoutAt = 0;
      }

      writeAttributes('checkout');
    };

    document.addEventListener('pointerdown', writeBeforeNavigation, true);
    document.addEventListener('click', writeBeforeNavigation, true);
    document.addEventListener('submit', function (event) {
      var form = event.target;
      var action = form && form.getAttribute ? form.getAttribute('action') : '';
      if (isCheckoutIntent(document.activeElement) || /\/cart\b|\/checkout\b|\/checkouts\b/i.test(action || '')) {
        writeAttributes('checkout');
      }
    }, true);
  }

  captureAndStoreAttribution();
  bindCartUpdateEvent();
  bindCheckoutHooks();
})();
